import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"

import ActionMenu from "src/components/ActionMenu"
import Button from "src/components/Button"
import Dropdown from "src/components/Dropdown"
import HeadingModalTitle from "src/components/Heading/HeadingModalTitle"
import Skeleton from "src/components/Skeleton"

import { useWindowSize } from "src/hooks/use_window_size"

import {
  DISABLED_ANCHOR_CLASSES,
  getSingleRowActions,
  getVisibleActions,
  isActionDisabled,
  sortViewFirst,
  toMenuItems,
} from "./actionUtils"
import { ActionPropType, ColumnPropType, SortConfigPropType } from "./types"

const CardView = ({
  columns,
  data,
  actions = [],
  isSelected,
  onToggleSelection,
  onActionClick,
  hasBulkActions,
  onRequestConfirm,
  selectedCount = 0,
  onSelectAll,
  onDeselectAll,
  allPageRowsSelected,
  showSelectAllBanner,
  onSelectAllAcrossPages,
  totalCount,
  pageSelectableCount,
  selection = [],
  isRowSelectable,
  sortConfig,
  isLoading = false,
  rowCount = 5,
  emptyState,
}) => {
  const bulkActions = actions.filter((action) => action.onClickBulk)
  const { isSmallScreen } = useWindowSize()
  const maxVisibleBulkActions = isSmallScreen ? 2 : 1
  const visibleBulkActions = bulkActions.slice(0, maxVisibleBulkActions)
  const overflowBulkActions = bulkActions.slice(maxVisibleBulkActions)

  // Get visible columns (not hidden via hide prop)
  const visibleColumns = columns.filter((col) => !col.hide)

  // Group columns by cardRole - all roles stack
  // Default to "detail" if no cardRole specified
  const getRole = (col) => col.cardRole || "detail"
  const byCardOrder = (a, b) =>
    (a.cardOrder ?? Infinity) - (b.cardOrder ?? Infinity)

  const titleCols = visibleColumns
    .filter((col) => getRole(col) === "title")
    .sort(byCardOrder)
  const subtitleCols = visibleColumns
    .filter((col) => getRole(col) === "subtitle")
    .sort(byCardOrder)
  const badgeCols = visibleColumns
    .filter((col) => getRole(col) === "badge")
    .sort(byCardOrder)
  const detailCols = visibleColumns
    .filter((col) => getRole(col) === "detail")
    .sort(byCardOrder)
  const footerCols = visibleColumns
    .filter((col) => getRole(col) === "footer")
    .sort(byCardOrder)
  // "hidden" columns are excluded from all groups

  const singleRowActions = getSingleRowActions(actions)

  // Card view uses cardRender if available, then render, then raw value
  const renderCellValue = (col, row) => {
    if (col.cardRender) {
      return col.cardRender(row)
    }
    if (col.render) {
      return col.render(row)
    }
    return row[col.key]
  }

  // Check if a cell has a meaningful value to display
  const hasValue = (col, row) => {
    const value = renderCellValue(col, row)
    if (value === null || value === undefined) return false
    if (value === "-") return false
    return !(typeof value === "string" && value.trim() === "")
  }

  const handleActionClick = (action, row) => {
    if (onActionClick) {
      onActionClick(action, row)
    } else if (action.onClickSingle) {
      action.onClickSingle(row)
    }
  }

  const handleBulkActionClick = (action) => {
    if (action.confirm && onRequestConfirm) {
      onRequestConfirm(action, null, selection)
      return
    }
    action.onClickBulk(selection)
  }

  // Render action menu with secondary actions (top-right of card)
  const renderActionMenu = (row, secondaryActions) => {
    if (secondaryActions.length === 0) return null

    const items = toMenuItems(secondaryActions, row, onActionClick)

    return <ActionMenu items={items} />
  }

  // Render primary actions as full-width buttons at bottom of card
  const renderPrimaryActions = (row, primaryActions) => {
    if (primaryActions.length === 0) return null

    return (
      <div className="flex w-full flex-col gap-2 pt-3">
        {primaryActions.map((action, index) => {
          const disabled = isActionDisabled(action, row)
          return action.href ? (
            <a
              key={index}
              href={action.href(row)}
              className={classNames("btn btn-secondary w-full text-center", {
                [DISABLED_ANCHOR_CLASSES]: disabled,
              })}
              onClick={(e) => disabled && e.preventDefault()}
            >
              {action.icon && <i className={`fa-kit fa-${action.icon} mr-2`} />}
              {action.label}
            </a>
          ) : (
            <Button
              key={index}
              variant={action.danger ? "danger" : "secondary"}
              fullWidth
              icon={action.icon ? `fa-${action.icon}` : undefined}
              onClick={() => handleActionClick(action, row)}
              disabled={disabled}
            >
              {action.label}
            </Button>
          )
        })}
      </div>
    )
  }

  const hasSelection = selectedCount > 0

  const sortableColumns = columns.filter((col) => col.sortable)
  const hasSorting = sortableColumns.length > 0 && sortConfig?.onColumnSort

  const sortDir = sortConfig?.sortDirection?.toLowerCase()

  const sortOptions = sortableColumns.map((col) => {
    const columnSortKey = col.sortKey || col.key
    const isSorted = sortConfig?.sortKey === columnSortKey
    return {
      label: col.header,
      value: columnSortKey,
      icon: isSorted
        ? sortDir === "desc"
          ? "sort-amount-down"
          : "sort-amount-up"
        : undefined,
      menuIcon: isSorted
        ? sortDir === "desc"
          ? "close-mdc"
          : "sort-amount-down"
        : undefined,
    }
  })

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <div data-testid="card-view">
        <div className="py-12 text-center" data-testid="empty-state">
          {emptyState || (
            <span className="text-gray-600">No results found</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="card-view"
      className="grid grid-cols-1 gap-x-2 gap-y-4 md:grid-cols-2"
    >
      {/* Toolbar: bulk selection + sort */}
      {(hasBulkActions || hasSorting) && (
        <div className="sticky top-0 z-10 flex flex-col rounded border-b bg-white md:col-span-2">
          <div className="flex flex-col gap-3 p-2">
            <div className="flex items-center justify-between gap-2">
              {hasBulkActions && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    small
                    onClick={onSelectAll}
                    disabled={allPageRowsSelected}
                  >
                    Select all
                  </Button>
                  <Button
                    variant="tertiary"
                    small
                    onClick={onDeselectAll}
                    disabled={!hasSelection}
                  >
                    Deselect all
                  </Button>
                  {hasSelection && (
                    <span className="text-sm text-gray-600">
                      <span className="font-semibold">{selectedCount}</span>{" "}
                      selected
                    </span>
                  )}
                </div>
              )}
              {hasSorting && (
                <Dropdown
                  label="Sort by"
                  value={sortConfig.sortKey}
                  options={sortOptions}
                  onSelect={(key) => sortConfig.onColumnSort(key)}
                />
              )}
            </div>
            {hasSelection && bulkActions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {visibleBulkActions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.danger ? "danger" : "secondary"}
                    small
                    icon={action.icon ? `fa-${action.icon}` : undefined}
                    onClick={() => handleBulkActionClick(action)}
                  >
                    {action.label}
                  </Button>
                ))}
                {overflowBulkActions.length > 0 && (
                  <ActionMenu
                    items={overflowBulkActions.map((action) => ({
                      label: action.label,
                      icon: action.icon,
                      onClick: () => handleBulkActionClick(action),
                    }))}
                  />
                )}
              </div>
            )}
          </div>
          {showSelectAllBanner && (
            <div className="flex items-center justify-center gap-1 border-t bg-blue-50 py-2 text-sm">
              <span className="text-gray-600">
                All {pageSelectableCount} on this page selected.
              </span>
              <Button variant="link" onClick={onSelectAllAcrossPages}>
                Select all {totalCount?.toLocaleString()} items
              </Button>
            </div>
          )}
        </div>
      )}
      {isLoading &&
        Array.from({ length: rowCount }, (_, i) => (
          <div
            key={`skeleton-card-${i}`}
            className="flex w-full flex-col rounded border bg-white p-4 shadow-md"
            data-testid="skeleton-card"
          >
            <div className="space-y-3">
              <Skeleton.Bar width="w-2/3" height="h-5" />
              <Skeleton.Bar width="w-1/2" height="h-3" />
              <Skeleton.Block width="w-1/3" height="h-6" />
              <div className="space-y-2 pt-2">
                <Skeleton.Bar width="w-full" height="h-3" />
                <Skeleton.Bar width="w-4/5" height="h-3" />
              </div>
            </div>
          </div>
        ))}
      {!isLoading &&
        data?.map((row) => {
          const visibleRowActions = sortViewFirst(
            getVisibleActions(singleRowActions, row)
          )
          const isPrimary = (action) => action.primary || action.role === "view"
          const primaryActions = visibleRowActions.filter(isPrimary)
          const secondaryActions = visibleRowActions.filter(
            (action) => !isPrimary(action)
          )

          return (
            <div
              key={row.id}
              className="flex w-full flex-col justify-between rounded border bg-white p-4 shadow-md"
            >
              <div className="flex w-full flex-col space-y-2">
                {/* Header: Titles, Subtitles, Checkbox/ActionMenu */}
                <div className="flex w-full items-start justify-between">
                  <div className="flex w-full flex-col">
                    {/* Titles stack */}
                    {titleCols.map(
                      (col) =>
                        hasValue(col, row) && (
                          <HeadingModalTitle key={col.key}>
                            {renderCellValue(col, row)}
                          </HeadingModalTitle>
                        )
                    )}
                    {/* Subtitles stack */}
                    {subtitleCols.map(
                      (col) =>
                        hasValue(col, row) && (
                          <span key={col.key} className="text-sm text-gray-600">
                            {renderCellValue(col, row)}
                          </span>
                        )
                    )}
                  </div>
                  <div className="ml-2 flex shrink-0 items-center gap-4">
                    {/* Checkbox for bulk selection */}
                    {hasBulkActions && (
                      <input
                        type="checkbox"
                        checked={isSelected?.(row.id) || false}
                        onChange={() => onToggleSelection?.(row.id)}
                        disabled={isRowSelectable && !isRowSelectable(row)}
                        className={classNames("m-0 h-4 w-4", {
                          "cursor-not-allowed rounded opacity-50":
                            isRowSelectable && !isRowSelectable(row),
                        })}
                      />
                    )}
                    {renderActionMenu(row, secondaryActions)}
                  </div>
                </div>

                {/* Badges stack */}
                {badgeCols.length > 0 && (
                  <div className="flex w-full flex-wrap gap-2">
                    {badgeCols.map(
                      (col) =>
                        hasValue(col, row) && (
                          <span className="w-full" key={col.key}>
                            {renderCellValue(col, row)}
                          </span>
                        )
                    )}
                  </div>
                )}

                {/* Details as key-value pairs in a 2-column grid */}
                {detailCols.length > 0 && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {detailCols.map(
                      (col) =>
                        hasValue(col, row) && (
                          <div key={col.key} className="text-xs">
                            {col.header && (
                              <span className="text-gray-500">
                                {col.header}:{" "}
                              </span>
                            )}
                            <span>{renderCellValue(col, row)}</span>
                          </div>
                        )
                    )}
                  </div>
                )}

                {/* Footer content (collapsibles, supplemental info) */}
                {footerCols.length > 0 && (
                  <div className="space-y-2 pt-2">
                    {footerCols.map(
                      (col) =>
                        hasValue(col, row) && (
                          <div key={col.key}>{renderCellValue(col, row)}</div>
                        )
                    )}
                  </div>
                )}
              </div>
              {/* Primary actions as full-width buttons at bottom */}
              {renderPrimaryActions(row, primaryActions)}
            </div>
          )
        })}
    </div>
  )
}

CardView.propTypes = {
  columns: PropTypes.arrayOf(ColumnPropType).isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  actions: PropTypes.arrayOf(ActionPropType),
  isSelected: PropTypes.func,
  onToggleSelection: PropTypes.func,
  onActionClick: PropTypes.func,
  hasBulkActions: PropTypes.bool,
  onRequestConfirm: PropTypes.func,
  selectedCount: PropTypes.number,
  onSelectAll: PropTypes.func,
  onDeselectAll: PropTypes.func,
  allPageRowsSelected: PropTypes.bool,
  showSelectAllBanner: PropTypes.bool,
  onSelectAllAcrossPages: PropTypes.func,
  totalCount: PropTypes.number,
  pageSelectableCount: PropTypes.number,
  selection: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  isRowSelectable: PropTypes.func,
  sortConfig: SortConfigPropType,
  isLoading: PropTypes.bool,
  rowCount: PropTypes.number,
  emptyState: PropTypes.node,
}

export default CardView
